import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { PulseResizeHandle } from '../PulseResizeHandle';

interface PulseHeaderProps {
  // Content slots
  label: React.ReactNode;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  menu?: React.ReactNode;

  // Layout
  align?: 'left' | 'center' | 'right';
  className?: string;

  // Grid/Sticky support
  isSticky?: boolean;
  stickyPosition?: 'left' | 'top' | 'both';

  // Sorting support (visual indicator only, actual sorting handled by menu)
  sortable?: boolean;
  sortDirection?: 'asc' | 'desc' | null;

  // Resizing support
  resizable?: boolean;
  onStartResize?: (e: React.MouseEvent) => void;
  isResizing?: boolean;
}

const PulseHeader = ({
  label,
  startDecorator,
  endDecorator,
  menu,
  align = 'left',
  className = '',
  isSticky = false,
  stickyPosition = 'top',
  sortable = false,
  sortDirection = null,
  resizable = false,
  onStartResize,
  isResizing = false,
}: PulseHeaderProps) => {
  // Extract string content for tooltip
  const tooltipText = typeof label === 'string' ? label : undefined;

  // Build sticky classes
  const stickyClasses = isSticky
    ? `sticky ${
        stickyPosition === 'both'
          ? 'top-0 left-0'
          : stickyPosition === 'left'
          ? 'left-0'
          : 'top-0'
      }`
    : '';

  return (
    <div
      role="columnheader"
      className={`px-4 py-2 group min-h-12 flex items-center relative ${stickyClasses} ${className}`}
    >
      <div
        className={`flex items-center gap-4 w-full ${
          align === 'center'
            ? 'justify-center'
            : align === 'right'
            ? 'justify-end'
            : 'justify-start'
        }`}
      >
        <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
          {startDecorator}
          <span
            className={`justify-start text-primary text-xs font-semibold font-['Inter'] leading-5 min-w-0 ${typeof label === 'string' ? 'truncate' : 'line-clamp-2'}`}
            title={tooltipText}
          >
            {label}
          </span>
          {endDecorator && (
            <span className="flex-shrink-0">{endDecorator}</span>
          )}
          {sortable && sortDirection && (
            <span className="flex-shrink-0 text-neutral-500 text-xs">
              <Icon icon={sortDirection === 'asc' ? faArrowUp : faArrowDown} />
            </span>
          )}
        </div>
        {menu && <div className="flex-shrink-0">{menu}</div>}
      </div>

      {resizable && onStartResize && (
        <PulseResizeHandle
          onStartResize={onStartResize}
          isResizing={isResizing}
        />
      )}
    </div>
  );
};

export default PulseHeader;
